import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import {
  Loader2, Search, UserPlus, CheckCheck, X, Mail, Package, PoundSterling,
  ClipboardPaste, Send, AlertTriangle, Link2,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Unallocated retail orders grouped by customer, with one-click "create account
// & invite" (allocates all of that customer's orders to a new ConsumerAccount
// and sends the sign-up email) and a bulk version for selected groups. Includes
// a "paste emails" tool to merge an external list of emails against the orphans
// by customer name before sending.

const fmt = (n) => `£${(Number(n) || 0).toFixed(2)}`;

function normName(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim(); }

export default function UnallocatedOrdersView({ orders, consumerAccounts, onProcessed }) {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [emailDrafts, setEmailDrafts] = useState({}); // key -> email
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(null); // key | 'bulk' | null
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');

  const existingEmails = useMemo(() => new Set((consumerAccounts || []).map((a) => (a.email || '').toLowerCase()).filter(Boolean)), [consumerAccounts]);

  // Orphan orders = no consumer_account_id, and the customer's email is not
  // already attached to an existing ConsumerAccount.
  const groups = useMemo(() => {
    const map = new Map();
    for (const o of orders || []) {
      if (o.consumer_account_id) continue;
      const email = (o.customer_email || '').toLowerCase().trim();
      if (email && existingEmails.has(email)) continue;
      const key = email || ('name::' + normName(o.customer_name));
      if (!key) continue;
      const g = map.get(key) || { key, email, name: o.customer_name || '—', order_ids: [], total: 0, count: 0, last: null, hasEmail: !!email, city: '', postcode: '', phone: '' };
      g.order_ids.push(o.id);
      g.total += Number(o.amount || 0);
      g.count += 1;
      const d = o.created_date ? new Date(o.created_date).getTime() : 0;
      if (d > (g.last || 0)) g.last = d;
      if (!g.city && o.delivery_city) g.city = o.delivery_city;
      if (!g.postcode && o.delivery_postcode) g.postcode = o.delivery_postcode;
      if (!g.phone && o.customer_phone) g.phone = o.customer_phone;
      if (!g.email && email) { g.email = email; g.hasEmail = true; }
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [orders, existingEmails]);

  const filtered = useMemo(() => {
    if (!search) return groups;
    const q = search.toLowerCase();
    return groups.filter((g) => g.name.toLowerCase().includes(q) || g.email.includes(q) || g.postcode.includes(q));
  }, [groups, search]);

  // Duplicate detection: for each orphan group, find a likely existing
  // ConsumerAccount by normalised name and/or postcode match so the admin can
  // link the orders to it instead of creating a duplicate account.
  const suggestions = useMemo(() => {
    const map = {};
    for (const g of groups) {
      const gn = normName(g.name);
      let best = null;
      for (const a of consumerAccounts || []) {
        const an = normName(a.customer_name);
        let score = 0;
        if (gn && an && gn === an && gn.length > 2) score = 3;
        else if (gn && an && Math.min(gn.length, an.length) > 4 && (gn.includes(an) || an.includes(gn))) score = 2;
        if (g.postcode && a.address_postcode) {
          const p1 = g.postcode.replace(/\s+/g, '').toLowerCase();
          const p2 = a.address_postcode.replace(/\s+/g, '').toLowerCase();
          if (p1 && p1 === p2) score += 1;
        }
        if (score >= 3) { best = a; break; }
      }
      if (best) map[g.key] = best;
    }
    return map;
  }, [groups, consumerAccounts]);

  const linkExisting = async (g, account) => {
    setBusy(g.key);
    try {
      const res = await base44.functions.invoke('consumerAccountsBulkAction', {
        mode: 'link_orders',
        account_id: account.id,
        order_ids: g.order_ids,
        send_invite: !!(emailDrafts[g.key] || g.email),
      });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Link failed');
      const r = res.data.results?.[0];
      toast({ title: `Linked ${g.order_ids.length} order${g.order_ids.length !== 1 ? 's' : ''} to ${account.customer_name}`, description: r?.invited ? 'Invite sent' : undefined });
      setEmailDrafts((p) => { const n = { ...p }; delete n[g.key]; return n; });
      if (onProcessed) await onProcessed();
    } catch (e) {
      toast({ title: 'Link failed', description: e.message, variant: 'destructive' });
    }
    setBusy(null);
  };

  const withEmail = groups.filter((g) => g.hasEmail).length;

  const setDraft = (key, val) => setEmailDrafts((p) => ({ ...p, [key]: val }));

  const buildBatchItem = (g) => ({
    email: (emailDrafts[g.key] || g.email || '').trim().toLowerCase(),
    customer_name: g.name,
    address_line1: '',
    address_city: g.city,
    address_postcode: g.postcode,
    phone: g.phone,
    order_ids: g.order_ids,
  });

  const runCreate = async (groupList, label) => {
    const batch = groupList.map(buildBatchItem).filter((b) => b.customer_name);
    if (!batch.length) return;
    setBusy(label);
    try {
      const res = await base44.functions.invoke('consumerAccountsBulkAction', { mode: 'create_and_invite', send_invite: true, batch });
      const data = res.data || {};
      const ok = (data.results || []).filter((r) => r.ok).length;
      const fail = (data.results || []).filter((r) => !r.ok);
      if (ok) toast({ title: `${ok} account${ok !== 1 ? 's' : ''} created & invited`, description: fail.length ? `${fail.length} failed` : undefined });
      if (fail.length) toast({ title: 'Some failed', description: fail.map((f) => f.error).join('; ').slice(0, 120), variant: 'destructive' });
      setEmailDrafts((p) => { const n = { ...p }; groupList.forEach((g) => delete n[g.key]); return n; });
      setSelected(new Set());
      if (onProcessed) await onProcessed();
    } catch (e) {
      toast({ title: 'Action failed', description: e.message, variant: 'destructive' });
    }
    setBusy(null);
  };

  const onSingle = (g) => runCreate([g], g.key);
  const onBulk = () => {
    const sel = groups.filter((g) => selected.has(g.key));
    if (!sel.length) return;
    runCreate(sel, 'bulk');
  };

  const toggle = (key) => setSelected((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // Paste-emails tool: parse lines like "Name, email" / "Name: email" / "Name <email>"
  // and auto-fill the email draft for any orphan group whose name matches.
  const applyPastedEmails = () => {
    const lines = pasteText.split(/\n+/).map((l) => l.trim()).filter(Boolean);
    let matched = 0;
    const drafts = { ...emailDrafts };
    for (const line of lines) {
      // extract email
      const emailMatch = line.match(/[^\s@]+@[^\s@]+\.[^\s@]+/);
      if (!emailMatch) continue;
      const email = emailMatch[0].toLowerCase();
      const namePart = line.replace(email, '').replace(/[<>,:|\t]+/g, ' ').replace(/\s+/g, ' ').trim();
      const target = groups.find((g) => g.name.toLowerCase().includes(namePart.toLowerCase()) || normName(g.name) === normName(namePart));
      if (target) { drafts[target.key] = email; matched++; }
    }
    setEmailDrafts(drafts);
    setPasteText('');
    setShowPaste(false);
    toast({ title: matched ? `${matched} email${matched !== 1 ? 's' : ''} matched` : 'No matches found', description: matched ? 'Review and send invites below' : 'Check the name format matches your orders' });
  };

  return (
    <div className="space-y-5">
      <div className="border border-white/10 bg-white/[0.02] p-4">
        <p className="text-white/50 text-xs leading-relaxed">
          <span className="text-white/70 font-bold">Unallocated orders</span> are retail sales with no linked consumer account. Group them by customer, add an email, then create their account and send a sign-up invite — their orders are allocated automatically. Paste a list of customer emails to fill them in bulk.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="border border-white/10 bg-[#111115] p-4 text-center">
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">UNALLOCATED CUSTOMERS</p>
          <p className="text-white font-black text-2xl">{groups.length}</p>
        </div>
        <div className="border border-cyan-400/20 bg-cyan-400/[0.03] p-4 text-center">
          <p className="text-cyan-400/70 text-[10px] font-bold tracking-widest mb-1">WITH EMAIL</p>
          <p className="text-cyan-400 font-black text-2xl">{withEmail}</p>
        </div>
        <div className="border border-primary/20 bg-primary/[0.03] p-4 text-center">
          <p className="text-primary/70 text-[10px] font-bold tracking-widest mb-1">TOTAL UNALLOCATED SPEND</p>
          <p className="text-primary font-black text-2xl">{fmt(groups.reduce((s, g) => s + g.total, 0))}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, postcode…" className="seo-input pl-9" />
        </div>
        <button onClick={() => setShowPaste((s) => !s)} className="inline-flex items-center justify-center gap-1.5 border border-white/15 hover:border-white/30 text-white/60 hover:text-white font-bold text-[10px] tracking-widest px-4 py-2 transition-colors">
          <ClipboardPaste className="w-3.5 h-3.5" /> PASTE EMAILS
        </button>
        {selected.size > 0 && (
          <button onClick={onBulk} disabled={busy === 'bulk'} className="inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-4 py-2 transition-colors disabled:opacity-50">
            {busy === 'bulk' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} CREATE & INVITE {selected.size} SELECTED
          </button>
        )}
      </div>

      {showPaste && (
        <div className="border border-white/10 bg-[#111115] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-primary text-[10px] font-bold tracking-widest">PASTE CUSTOMER EMAILS</p>
            <button onClick={() => setShowPaste(false)} className="text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-white/40 text-xs">One per line, e.g. <span className="text-white/60">John Smith, john@example.com</span>. Names are matched against the customer names below.</p>
          <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={6} placeholder={"John Smith, john@example.com\nJane Doe, jane@example.com"} className="seo-input w-full resize-y" />
          <button onClick={applyPastedEmails} className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-4 py-2">
            <CheckCheck className="w-3.5 h-3.5" /> MATCH & FILL EMAILS
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-white/30 border border-white/5 bg-[#111115]">
          <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-bold tracking-widest">NO UNALLOCATED ORDERS</p>
          <p className="text-xs mt-1">Every retail order is linked to a consumer account</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((g) => {
            const draft = emailDrafts[g.key] || '';
            const effectiveEmail = (draft || g.email || '').trim().toLowerCase();
            const canInvite = !!effectiveEmail && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(effectiveEmail);
            const isSel = selected.has(g.key);
            return (
              <div key={g.key} className={`border bg-[#111115] p-4 ${isSel ? 'border-primary/40' : 'border-white/10'}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <input type="checkbox" checked={isSel} onChange={() => toggle(g.key)} className="w-4 h-4 accent-primary mt-1.5 cursor-pointer" />
                  <div className="w-9 h-9 bg-primary/10 flex items-center justify-center shrink-0 text-primary font-bold text-sm">
                    {g.name.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm">{g.name}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-white/40">
                      <span className="flex items-center gap-1"><Package className="w-3 h-3" />{g.count} order{g.count !== 1 ? 's' : ''}</span>
                      <span className="flex items-center gap-1 text-primary"><PoundSterling className="w-3 h-3" />{fmt(g.total)}</span>
                      {g.postcode && <span>{g.postcode}</span>}
                      {g.phone && <span>{g.phone}</span>}
                      {g.last && <span>{new Date(g.last).toLocaleDateString('en-GB')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    {g.hasEmail && !draft ? (
                      <span className="text-cyan-400/80 text-xs flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{g.email}</span>
                    ) : null}
                    <input
                      type="email"
                      value={draft}
                      onChange={(e) => setDraft(g.key, e.target.value)}
                      placeholder={g.email || 'add email…'}
                      className="seo-input w-48"
                    />
                    <button
                      onClick={() => onSingle(g)}
                      disabled={!canInvite || busy === g.key || busy === 'bulk'}
                      title={canInvite ? 'Create account, allocate orders & send invite' : 'Add a valid email first'}
                      className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {busy === g.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} CREATE & INVITE
                    </button>
                  </div>
                </div>
                {!g.hasEmail && !draft && (
                  <p className="mt-2 ml-12 flex items-center gap-1.5 text-amber-400/70 text-[10px]"><AlertTriangle className="w-3 h-3" /> No email on these orders — add one (or use Paste Emails) before inviting.</p>
                )}
                {suggestions[g.key] && (
                  <div className="mt-2 ml-12 flex items-center gap-2 text-amber-400/80 text-[10px] flex-wrap">
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    <span>Possible match: <span className="text-white font-bold">{suggestions[g.key].customer_name}</span> ({suggestions[g.key].sage_account_ref})</span>
                    <button onClick={() => linkExisting(g, suggestions[g.key])} disabled={busy === g.key} className="inline-flex items-center gap-1 border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400 hover:text-black text-amber-400 font-bold text-[10px] tracking-widest px-2 py-1 disabled:opacity-50">
                      {busy === g.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} LINK INSTEAD
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}